




import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {DateTime} from "luxon";
import {Chart, ChartItem, registerables} from "chart.js";

const MS_IN_DAY = 24 * 3600 * 1000;

@Component({})
export default class BaseStationChart extends Vue {

  protected chart:any;

  @Prop({ default: {}, type: String}) lrrId!: string;
  @Prop({ default: {}, type: Object}) bus!:Vue;

  private loading:boolean = true;
  private range:string = '1d';
  private interval:string = '1h';
  protected timeType:string = 'gw';

  private startSec:number = 0;
  private finishSec:number = 0;
  private intervalSec:number = 0;

  private currentZoomedStartSec:number = 0;
  private currentZoomedFinishSec:number = 0;
  private currentZoomedIntervalSec:number = 0;

  private prevZoomedStartSec:number = 0;
  private prevZoomedFinishSec:number = 0;
  private prevZoomedIntervalSec:number = 3600;

  protected chartData:any = {};
  protected chartOptions:any = {
    aspectRatio: 5,
    layout: {
      padding: 0,
    },
    elements: {
      point: {
        radius: 2,
      },
      line: {
        stepped: true,
      },
    },
    plugins: {
      zoom: {
        limits: {
          x: {
            // @ts-ignore
            min: DateTime.now().minus(90 * MS_IN_DAY).toMillis(),
            // @ts-ignore
            max: DateTime.now(),
            minRange: 3600 * 1000,
          },
        },
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'shift',
          onPanComplete: (chart:Chart) => {
            this.onZoomCompleted(chart);
          },
        },
        zoom: {
          wheel: {
            enabled: true,
            modifierKey: 'shift',
          },
          pitch: {
            enabled: true,
          },
          drag: {
            enabled: true,
            backgroundColor: 'rgba(225,225,225,0.9)'
          },
          mode: 'x',
          onZoomComplete: (chart:Chart) => {
            this.onZoomCompleted(chart);
          },
        },
      },
    },
    scales: {
      yAxis: {
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        }
      },
      xAxis: {
        type: 'time',
        grid: {
          offset: false,
        },
        ticks: {
          source: 'auto',
        },
        time: {
          unit: 'hour',
          displayFormats: {
            minute: 'DD, HH:mm',
            hour: 'DD, HH:mm',
            day: 'DD',
          },
          tooltipFormat: 'DD, HH:mm',
        },
      },
    }
  };

  private onZoomCompleted(chart:any) {


    if ( chart.chart.scales && chart.chart.scales.xAxis ) {
      if (chart.chart.scales.xAxis.max - chart.chart.scales.xAxis.min < MS_IN_DAY) {
        this.chart.options.scales.xAxis.time.unit = 'minute';
      } else if (chart.chart.scales.xAxis.max - chart.chart.scales.xAxis.min < 5 * MS_IN_DAY) {
        this.chart.options.scales.xAxis.time.unit = 'hour';
      } else {
        this.chart.options.scales.xAxis.time.unit = 'day';
      }


      this.currentZoomedStartSec = Math.floor(chart.chart.scales.xAxis.min / 1000);
      this.currentZoomedFinishSec = Math.floor(chart.chart.scales.xAxis.max / 1000);
      this.currentZoomedIntervalSec = this.calcIntervalFromStartFinish(
          this.currentZoomedStartSec,
          this.currentZoomedFinishSec);

      if (
          this.currentZoomedStartSec !== this.prevZoomedStartSec ||
          this.currentZoomedFinishSec !== this.prevZoomedFinishSec ||
          this.currentZoomedIntervalSec !== this.prevZoomedIntervalSec ) {
        this.refreshChart(this.lrrId,
            this.currentZoomedStartSec,
            this.currentZoomedFinishSec,
            this.currentZoomedIntervalSec,
            this.timeType);

        this.bus.$emit('on-zoom', this, this.currentZoomedStartSec, this.currentZoomedFinishSec);
      }

      this.prevZoomedStartSec = this.currentZoomedStartSec;
      this.prevZoomedFinishSec = this.currentZoomedFinishSec;
      this.prevZoomedIntervalSec = this.currentZoomedIntervalSec;
    }
  }

  private async refreshChart(lrrId: string,
                             startSec: number,
                             finishSec: number,
                             intervalSec: number,
                             timeType: string) {
    if ( this.chart ) {
      this.chart.data = await this.fetchChartData(lrrId,
          startSec,
          finishSec,
          intervalSec,
          timeType);
      this.zoomToRange(this.chart, startSec, finishSec);
      this.chart.update();
    }
  }

  private zoomToRange(chart: any,
                      startSec: number,
                      finishSec: number) {
    if ( chart.options && chart.options.scales && chart.options.scales.xAxis ) {


      chart.options.scales.xAxis.min = startSec * 1000;
      chart.options.scales.xAxis.max = finishSec * 1000;

      // if ( chart.options.plugins
      //     && chart.options.plugins.zoom
      //     && chart.options.plugins.zoom.limits
      //     && chart.options.plugins.zoom.limits.x ) {
      //   chart.options.plugins.zoom.limits.x.min = startSec * 1000;
      //   chart.options.plugins.zoom.limits.x.max = finishSec * 1000;
      // }

      chart.options.scales.xAxis.time.unit
          = this.calcXAxisTimeUnitBasedOnRange(startSec, finishSec);
    }
  }

  protected calcInterval(range: string, origInterval: string): string {
    return origInterval;
  }

  private calcXAxisTimeUnitBasedOnRange(startSec: number, finishSec: number): string {
    const rangeAsNumberOfHours = (finishSec - startSec) / 3600;
    if (rangeAsNumberOfHours > 5 * 24) {
      return 'day';
    } else if (rangeAsNumberOfHours > 2 * 24) {
      return  'hour';
    }
    return  'minute';
  }

  protected async createChart(ctx: ChartItem) {
    // must be implemented in an inherited class
    throw new Error('must be implemented in a inherited class');
  }

  private async fetchChartData(lrrId: string,
                               startSec: number,
                               finishSec: number,
                               intervalSec: number,
                               timeType: string):Promise<any> {
    this.loading = true;
    try {
      const chartData = await this.fetchChartDataImpl(lrrId, startSec, finishSec, intervalSec, timeType);
      this.loading = false;
      return chartData;
    } catch (error) {
      this.loading = false;
      console.log(`cannot fetch chart data due to error ${error}`);
    }
  }

  protected calcIntervalFromStartFinish(startSec: number, finishSec: number): number {
    throw new Error('must be implemented in inherited class');
  }

  private calcStartFinishFromRange(range: string, interval: string): number[] {
    const rangeAsNumberOfDays = Number(range.slice(0, -1));

    // @ts-ignore
    const finish = DateTime.now();
    const start = finish.minus({days: rangeAsNumberOfDays});

    const intervalAsNumber = Number(interval.slice(0, -1));
    const intervalAsEntity = interval.slice(-1);

    // console.log(`${intervalAsNumber} - ${intervalAsEntity}`);

    let intervalSec = 60;
    if (intervalAsEntity === 'm') {
      intervalSec = intervalAsNumber * 60;
    } else if (intervalAsEntity === 'h') {
      intervalSec = intervalAsNumber * 3600;
    } else if  (intervalAsEntity === 'd') {
      intervalSec = intervalAsNumber * 24 * 3600;
    }

    return [
      Math.round(start.toSeconds()),
      Math.round(finish.toSeconds()),
      intervalSec,
    ];
  }

  protected async fetchChartDataImpl(lrrId: string,
                                     start: number,
                                     finish: number,
                                     interval: number,
                                     timeType: string):Promise<any> {
    // must be implemented in an inherited class
    throw new Error('must be implemented in a inherited class');
  }

  @Watch("lrrId")
  async onLrrIdChanged(newLrrId:string) {
    await this.refreshChart(this.lrrId,
        this.startSec,
        this.finishSec,
        this.intervalSec,
        this.timeType);
  }

  created() {
    Chart.register(...registerables);

    this.interval = this.calcInterval(this.range, this.interval);

    [this.startSec, this.finishSec, this.intervalSec]  = this.calcStartFinishFromRange(this.range, this.interval);

    this.bus.$on('on-zoom', async (sourceOfEvent: any, startSec:number, finishSec:number) => {
      this.startSec = startSec;
      this.finishSec = finishSec;
      this.intervalSec = this.calcIntervalFromStartFinish(this.startSec, this.finishSec);
      if ( sourceOfEvent !== this ) {
        await this.refreshChart(this.lrrId,
            this.startSec,
            this.finishSec,
            this.intervalSec,
            this.timeType);
      }
    });

    this.bus.$on('refresh-chart', async () => {
      await this.refreshChart(this.lrrId,
          this.startSec,
          this.finishSec,
          this.intervalSec,
          this.timeType);
    });

    this.bus.$on('change-range', async (range:string, interval:string) => {
      this.range = range;
      this.interval = this.calcInterval(this.range, interval);
      [this.startSec, this.finishSec, this.intervalSec] = this.calcStartFinishFromRange(this.range, this.interval);
      await this.refreshChart(this.lrrId,
          this.startSec,
          this.finishSec,
          this.intervalSec,
          this.timeType);
    });

    this.bus.$on('time-type-changed', async (timeType:string) => {
      this.timeType = timeType;
      await this.refreshChart(this.lrrId,
          this.startSec,
          this.finishSec,
          this.intervalSec,
          this.timeType);
    });
  }
}
